import React from "react";
import Contact from "../contact";
import arrowIcon from "../../images/home/arrow-icon.svg";
import benifitIcon from "../../images/individual/benifitIcon.png";
import { FaBriefcase, FaCalendarAlt, FaExternalLinkAlt, FaUsers, FaArrowUp, FaMapMarkerAlt, FaChartLine, FaGlobe, FaCheckCircle, FaInfoCircle, FaClock, FaHandshake } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const EmployerSponsoredVisas202425 = () => {
  return (
    <div>
      <Helmet>
        <title>Employer-Sponsored Visas 2024-25: Increased Planning Levels - Immiget Global</title>
        <meta
          name="description"
          content="Australia increases employer-sponsored visa planning to 44,000 places for 2024-25. Learn about the Temporary Residence Transition Stream and pathways to permanent residency."
        />
        <meta 
          name="keywords" 
          content="employer sponsored visa 2024-25, subclass 186 visa, subclass 187 visa, temporary residence transition, permanent residency pathway" 
        />
      </Helmet>
      
      <div className="w-full h-90 font-Hellix bg-cover bg-center bg-image">
        <div className="max-w-[1440px] px-4 md:px-[75px] m-auto h-full">
          <div className="flex flex-col gap-2 md:gap-2 pt-10 md:pt-20 pb-[90px] h-full justify-center">
            <div className="text-white hellix-font text-center font-semibold text-[22px] md:text-[54px]">
              Employer-Sponsored Visas 2024–25
            </div>
            <div className="text-white hellix-font text-center font-medium text-[16px] md:text-[24px] mb-4">
              Increased Planning Levels & Clearer Pathways
            </div>
            <div className="flex justify-center gap-2 md:gap-4">
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium">
                <Link to="/" className="hover:underline">HOME</Link>
              </div>
              <div className="flex items-center">
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
              </div>
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium">
                <Link to="/news" className="hover:underline">NEWS</Link>
              </div>
              <div className="flex items-center">
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
              </div>
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium uppercase">
                Employer-Sponsored Visas
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <div className="container max-w-1440px mx-auto px-4 py-6 xl:py-10 lg:px-14">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue text-white p-3 rounded-full">
              <FaBriefcase className="text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-blue text-sm hellix-font font-medium">
                <FaCalendarAlt className="text-xs" />
                <span>2024-25</span>
              </div>
              <div className="text-blue text-sm hellix-font">Employer Visa Update</div>
            </div>
          </div>
          
          <h1 className="xl:text-[42px] text-[32px] text-blue font-bold hellix-font mb-6 leading-tight">
            Employer-Sponsored Visas: Increased Planning Levels for 2024–25
          </h1>
          
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <FaInfoCircle className="text-blue text-xl mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Major Increase</h3>
                <p className="text-blue hellix-font text-lg leading-relaxed">
                  The planning level for Employer-Sponsored visas has been increased to <strong>44,000 visas for 2024–25</strong>, up from 36,825 in the previous year. This represents a <strong>19.5% increase</strong> in allocation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-1440px mx-auto px-4 lg:px-14">
        <div className="max-w-4xl mx-auto">
          
          {/* Overview Section */}
          <section className="mb-12">
            <div className="flex gap-2 mb-6">
              <div className="flex items-center">
                <img src={benifitIcon} alt="benefits" />
              </div>
              <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font">
                Overview of Changes
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-blue hellix-font text-lg leading-relaxed mb-6">
                The significant increase in employer-sponsored visa allocations demonstrates the Australian Government's commitment to providing clearer pathways to permanent residency for temporary migrants. This expansion particularly benefits those seeking to transition from temporary to permanent residence.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 my-8">
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-blue">
                  <div className="flex items-center gap-3 mb-4">
                    <FaArrowUp className="text-green-600 text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">2024-25</h3>
                  </div>
                  <p className="text-3xl font-bold text-blue hellix-font">44,000</p>
                  <p className="text-blue hellix-font text-sm">Visa places allocated</p>
                </div>
                
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3 mb-4">
                    <FaClock className="text-gray-600 text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">Previous Year</h3>
                  </div>
                  <p className="text-3xl font-bold text-blue hellix-font">36,825</p>
                  <p className="text-blue hellix-font text-sm">Visa places allocated</p>
                </div>
                
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-green-500">
                  <div className="flex items-center gap-3 mb-4">
                    <FaChartLine className="text-green-600 text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">Increase</h3>
                  </div>
                  <p className="text-3xl font-bold text-green-600 hellix-font">+19.5%</p>
                  <p className="text-blue hellix-font text-sm">Growth in allocation</p>
                </div>
              </div>
            </div>
          </section>

          {/* Temporary Residence Transition Stream */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6 flex items-center gap-2">
              <FaHandshake className="text-blue" />
              Temporary Residence Transition Stream
            </h2>
            
            <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6 mb-6">
              <p className="text-blue hellix-font text-lg leading-relaxed mb-4">
                This expansion particularly supports the government's initiative to provide a clearer pathway to permanent residency through the <strong>Temporary Residence Transition Stream</strong>. This stream is designed for temporary visa holders who are already in Australia and sponsored by their employers.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-4">
                  <h3 className="text-blue font-semibold hellix-font text-lg">Benefits for Applicants:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font">Faster processing for certain applicants</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font">Clear pathway from temporary to permanent residence</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font">Reduced uncertainty for families</span>
                    </li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-blue font-semibold hellix-font text-lg">Benefits for Employers:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font">Retain skilled employees long-term</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font">Streamlined sponsorship processes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font">Access to global talent pool</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Visa Categories */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Employer-Sponsored Visa Categories
            </h2>
            
            <div className="grid gap-6">
              <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-blue">
                <div className="flex items-start gap-4">
                  <div className="bg-blue text-white p-3 rounded-lg flex-shrink-0">
                    <FaBriefcase className="text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-blue font-semibold hellix-font text-xl mb-3">
                      Employer Nomination Scheme (ENS) - Subclass 186
                    </h3>
                    <p className="text-blue hellix-font mb-4 leading-relaxed">
                      For skilled workers nominated by their Australian employer for a permanent position. Includes Temporary Residence Transition, Direct Entry, and Labour Agreement streams.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-blue-100 text-blue px-3 py-1 rounded-full text-sm hellix-font">Permanent Visa</span>
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm hellix-font">Priority Processing</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-green-500">
                <div className="flex items-start gap-4">
                  <div className="bg-green-600 text-white p-3 rounded-lg flex-shrink-0">
                    <FaMapMarkerAlt className="text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-blue font-semibold hellix-font text-xl mb-3">
                      Regional Sponsored Migration Scheme (RSMS) - Subclass 187
                    </h3>
                    <p className="text-blue hellix-font mb-4 leading-relaxed">
                      For skilled workers nominated by employers in designated regional areas. Designed to address skill shortages in regional Australia and support economic development outside major cities.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-blue-100 text-blue px-3 py-1 rounded-full text-sm hellix-font">Permanent Visa</span>
                      <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm hellix-font">Regional Only</span>
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm hellix-font">Priority Processing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Impact on Labor Market */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6 flex items-center gap-2">
              <FaUsers className="text-blue" />
              Labor Market Impact
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6">
                <h3 className="text-blue font-semibold hellix-font text-lg mb-4">Skills Shortage Sectors</h3>
                <p className="text-blue hellix-font mb-4">
                  The increased allocation will particularly benefit sectors experiencing critical skill shortages:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                    <span className="text-blue hellix-font">Healthcare and aged care</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                    <span className="text-blue hellix-font">Information technology</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                    <span className="text-blue hellix-font">Engineering and construction</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                    <span className="text-blue hellix-font">Education and training</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-white rounded-lg p-6">
                <h3 className="text-blue font-semibold hellix-font text-lg mb-4">Economic Benefits</h3>
                <p className="text-blue hellix-font mb-4">
                  The expansion will contribute to Australia's economic recovery and growth:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                    <span className="text-blue hellix-font">Increased productivity</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                    <span className="text-blue hellix-font">Innovation and knowledge transfer</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                    <span className="text-blue hellix-font">Tax revenue generation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                    <span className="text-blue hellix-font">Job creation for locals</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          
          


          {/* Official Resources */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Official Resources
            </h2>
            
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-blue hellix-font mb-4">
                For detailed information about employer-sponsored visas and the latest updates:
              </p>
              <a
                href="https://immi.homeaffairs.gov.au/what-we-do/migration-program-planning-levels"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue font-medium hellix-font hover:text-blue-700 transition-colors"
              >
                <FaExternalLinkAlt className="text-sm" />
                Department of Home Affairs - Migration Program Planning Levels
              </a>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Related Immigration News
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/news/migration-program-2024-25" className="bg-white rounded-lg shadow-custom-white p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <FaArrowUp className="text-blue text-lg" />
                  <span className="text-blue text-sm hellix-font font-medium">Migration Program</span>
                </div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Migration Program 2024–25: Focus on Skills and Regional Australia</h3>
                <p className="text-blue hellix-font text-sm">185,000 places allocated with 71% to Skill stream addressing critical shortages...</p>
              </Link>
              
              <Link to="/news" className="bg-white rounded-lg shadow-custom-white p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <FaGlobe className="text-blue text-lg" />
                  <span className="text-blue text-sm hellix-font font-medium">All News</span>
                </div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">View All Immigration News</h3>
                <p className="text-blue hellix-font text-sm">Stay updated with the latest immigration policies and opportunities...</p>
              </Link>
            </div>
          </section>
        </div>
      </div>

      {/* Contact Section */}
      <Contact source="Employer-Sponsored Visas 2024-25 Blog" />
    </div>
  );
};

export default EmployerSponsoredVisas202425;